/**
 * Canonical Job Cost Read Bridge (Phase A)
 * ----------------------------------------
 * READ-ONLY consolidation of every cost the platform can attribute or
 * project against a single job, shaped into a uniform `JobCostLine` list.
 *
 * This is NOT:
 *   - an accounting engine
 *   - an allocator engine
 *   - a write path
 *   - a reserve policy
 *
 * Rules (per OPERATIONAL_COST_SOURCE_MAP §9):
 *   - Never silently zero an untracked category. Surface it as a missing line
 *     and exclude it from totals.
 *   - Lifecycle precedence per category: realized > committed > projected.
 *     Never sum realized + projected for the same category.
 *   - No allocator runs inside this resolver.
 *   - Only existing canonical sources are read. No new write paths.
 */
import type { Job } from "@/data/types";
import type { PurchaseOrder } from "@/data/VendorPOContext";
import type { ReceivingRecord } from "@/data/WarehouseInventoryContext";
import {
  getJobRealizedMaterialCost,
  type MaterialCoverage,
} from "@/data/jobCost/materialCostBridge";
import {
  getJobExpenseLedgerRollup,
  hasRealizedExpense,
  type ExpenseLedgerEntry,
  type ExpenseLedgerCategory,
} from "@/data/jobCost/expenseLedgerBridge";

// ── Shape ────────────────────────────────────────────────────────────

export type CostCategory =
  | "offer_compensation"
  | "materials"
  | "marketing_cac"
  | "permits"
  | "financing_fees"
  | "warranty_remediation"
  | "callback_labor"
  | "dumpster_disposal"
  | "fuel_logistics"
  | "field_add"
  | "sales_commission"
  | "admin_allocation"
  | "warehouse_allocation"
  | "vehicle_allocation"
  | "software_allocation"
  | "insurance_allocation"
  | "office_allocation"
  | "misc_allocation"
  | "warranty_reserve"
  | "callback_reserve"
  | "bad_debt_reserve"
  | "holdback_reserve";

export type CostLifecycle =
  | "projected"
  | "committed"
  | "realized"
  | "allocated"
  | "reserve";

export type CostSource =
  | "job_financials"
  | "job_payout"
  | "marketing_attribution"
  | "jobspec_bom"
  | "vendor_po"
  | "expense_ledger"
  | "commission_engine"
  | "permitting_module"
  | "change_order"
  | "allocator"
  | "reserve_policy"
  | "none";

export type CostReliability =
  | "actual"
  | "estimated"
  | "projected"
  | "allocated"
  | "missing";

export type CostVisibility = "finance" | "ops" | "dispatcher" | "sales";

export interface JobCostLine {
  category: CostCategory;
  label: string;
  amount: number | null;
  lifecycle: CostLifecycle;
  source: CostSource;
  reliability: CostReliability;
  visibility: CostVisibility[];
  includedInGross: boolean;
  includedInNet: boolean;
  notes?: string;
}

export interface JobCostTotals {
  contractValue: number;
  directKnownTotal: number;
  allocationsKnownTotal: number;
  projectedGrossContribution: number;
  projectedGrossMargin: number;
  /** Only present when ALL gross-eligible direct categories are realized. */
  realizedGrossContribution: number | null;
  projectedNetContribution: number;
  projectedNetMargin: number;
  missingCostCount: number;
  projectedCostCount: number;
  realizedCostCount: number;
}

export interface JobCostBreakdown {
  jobId: string;
  generatedAt: string;
  contractValue: number;
  hasContractValue: boolean;
  lines: JobCostLine[];
  direct: JobCostLine[];
  allocations: JobCostLine[];
  reserves: JobCostLine[];
  totals: JobCostTotals;
  coverage: {
    trackedCategories: CostCategory[];
    untrackedCategories: CostCategory[];
  };
  disclaimer: string;
}

// ── Internal helpers ─────────────────────────────────────────────────

const DISCLAIMER =
  "Projected decision view — not accounting truth. Final accounting may differ once invoices, financing, service, and closeout costs are reconciled.";

const FINANCE_ONLY: CostVisibility[] = ["finance"];
const FINANCE_OPS: CostVisibility[] = ["finance", "ops"];
const FINANCE_OPS_DISPATCH: CostVisibility[] = ["finance", "ops", "dispatcher"];

interface BuildLineInput {
  category: CostCategory;
  label: string;
  amount: number | null | undefined;
  lifecycle: CostLifecycle;
  source: CostSource;
  reliability: CostReliability;
  visibility: CostVisibility[];
  includedInGross: boolean;
  includedInNet: boolean;
  notes?: string;
}

function buildLine(input: BuildLineInput): JobCostLine {
  const present = typeof input.amount === "number" && input.amount > 0;
  return {
    category: input.category,
    label: input.label,
    amount: present ? (input.amount as number) : null,
    lifecycle: input.lifecycle,
    source: present ? input.source : "none",
    reliability: present ? input.reliability : "missing",
    visibility: input.visibility,
    includedInGross: input.includedInGross,
    includedInNet: input.includedInNet,
    notes: input.notes,
  };
}

// ── Resolver ─────────────────────────────────────────────────────────

export interface ResolveJobCostOptions {
  /**
   * Live draft Offer Compensation override (e.g. from CompensationBuilder).
   * When provided, the offer_compensation line is built from this value and
   * tagged as projected/estimated, not realized.
   */
  draftOfferCompensation?: number | null;
  /**
   * Phase B — optional procurement signal so the materials line can be
   * promoted from `estimated` to `committed`/`realized`. When omitted, the
   * resolver falls back to `Job.financials.estimatedMaterialCost` exactly
   * like Phase A.
   */
  materialProcurement?: {
    purchaseOrders?: PurchaseOrder[];
    receivingRecords?: ReceivingRecord[];
  };
  /**
   * Phase C — optional realized expense ledger entries. When provided,
   * categories with ≥1 valid entry are promoted from `missing` to
   * `realized` (e.g. permits, financing fees, callback labor, warranty,
   * field-add, sales commission, etc.). Absence ≠ zero — categories
   * with no entries keep their existing `missing` line.
   */
  expenseLedgerEntries?: ExpenseLedgerEntry[];
}

export function getJobCostBreakdown(
  job: Job,
  options: ResolveJobCostOptions = {},
): JobCostBreakdown {
  const contractValue = job.financials?.contractValue ?? 0;
  const hasContractValue = contractValue > 0;

  // ── Offer Compensation (direct, lifecycle depends on draft vs saved) ──
  const savedOffer = job.payoutAmount ?? 0;
  const usingDraft =
    options.draftOfferCompensation !== undefined &&
    options.draftOfferCompensation !== null;
  const offerAmount = usingDraft
    ? (options.draftOfferCompensation as number)
    : savedOffer;

  const offerCompensation = buildLine({
    category: "offer_compensation",
    label: "Offer Compensation",
    amount: offerAmount > 0 ? offerAmount : null,
    lifecycle: usingDraft ? "projected" : savedOffer > 0 ? "committed" : "projected",
    source: "job_payout",
    reliability: usingDraft ? "projected" : savedOffer > 0 ? "actual" : "missing",
    visibility: FINANCE_OPS_DISPATCH,
    includedInGross: true,
    includedInNet: true,
    notes: usingDraft
      ? `Draft preview · saved ${savedOffer > 0 ? `$${savedOffer.toLocaleString()}` : "—"}`
      : undefined,
  });

  // ── Materials — Phase B: PO/receiving precedence over estimate ──
  const matBridge = getJobRealizedMaterialCost({
    job,
    purchaseOrders: options.materialProcurement?.purchaseOrders,
    receivingRecords: options.materialProcurement?.receivingRecords,
  });
  const matLabel =
    matBridge.coverage === "realized"
      ? "Materials (Realized)"
      : matBridge.coverage === "committed"
      ? "Materials (Committed PO)"
      : matBridge.coverage === "partial"
      ? "Materials (Partial Receipt)"
      : matBridge.coverage === "estimated"
      ? "Materials (Estimated)"
      : "Materials";
  const matLifecycle: CostLifecycle =
    matBridge.coverage === "realized"
      ? "realized"
      : matBridge.coverage === "committed" || matBridge.coverage === "partial"
      ? "committed"
      : "projected";
  const matReliability: CostReliability =
    matBridge.coverage === "realized"
      ? "actual"
      : matBridge.coverage === "committed"
      ? "estimated"
      : matBridge.coverage === "partial"
      ? "projected"
      : matBridge.coverage === "estimated"
      ? "estimated"
      : "missing";
  const matSource: CostSource =
    matBridge.source === "vendor_po"
      ? "vendor_po"
      : matBridge.source === "job_financials"
      ? "job_financials"
      : "none";
  const materials = buildLine({
    category: "materials",
    label: matLabel,
    amount: matBridge.amount ?? null,
    lifecycle: matLifecycle,
    source: matSource,
    reliability: matReliability,
    visibility: FINANCE_OPS,
    includedInGross: true,
    includedInNet: true,
    notes: matBridge.notes,
  });

  // ── Marketing CAC — realized at intake ──
  const marketingCost = job.marketing?.leadCost ?? 0;
  const marketing = buildLine({
    category: "marketing_cac",
    label: "Marketing Attribution",
    amount: marketingCost,
    lifecycle: "realized",
    source: "marketing_attribution",
    reliability: "actual",
    visibility: FINANCE_OPS,
    includedInGross: true,
    includedInNet: true,
    notes: job.marketing?.leadSource,
  });

  // ── Phase C: Expense ledger rollup (promotes missing → realized) ──
  const ledger = getJobExpenseLedgerRollup(job.id, options.expenseLedgerEntries ?? []);

  /**
   * Promote a per-category line from `missing` → `realized` when the
   * ledger has ≥1 valid entry. Falls back to the supplied missing-line
   * defaults otherwise. Never the other way around.
   */
  const ledgerLine = (
    category: CostCategory,
    ledgerCategory: ExpenseLedgerCategory,
    fallback: BuildLineInput,
  ): JobCostLine => {
    const bucket = ledger.byCategory[ledgerCategory];
    if (!bucket || bucket.amount <= 0) return buildLine(fallback);
    return buildLine({
      ...fallback,
      amount: bucket.amount,
      lifecycle: "realized",
      source: "expense_ledger",
      reliability: "actual",
      notes:
        `${bucket.entryCount} entr${bucket.entryCount === 1 ? "y" : "ies"} · ` +
        `source: ${bucket.source}`,
    });
  };

  const permits = ledgerLine("permits", "permits", {
    category: "permits",
    label: "Permit Fees",
    amount: null,
    lifecycle: "projected",
    source: "permitting_module",
    reliability: "missing",
    visibility: FINANCE_OPS,
    includedInGross: true,
    includedInNet: true,
    notes: "Permit lifecycle tracked; dollars not yet captured",
  });

  const financing = ledgerLine("financing_fees", "financing_fees", {
    category: "financing_fees",
    label: "Financing Fees",
    amount: null,
    lifecycle: "realized",
    source: "expense_ledger",
    reliability: "missing",
    visibility: FINANCE_ONLY,
    includedInGross: true,
    includedInNet: true,
    notes: "No expense_ledger entries for this job",
  });

  const warranty = ledgerLine("warranty_remediation", "warranty_remediation", {
    category: "warranty_remediation",
    label: "Warranty / Service Reserve",
    amount: null,
    lifecycle: "reserve",
    source: "reserve_policy",
    reliability: "missing",
    visibility: FINANCE_OPS,
    includedInGross: false,
    includedInNet: true,
    notes: "Reserve framework not yet built (Phase D)",
  });
  // When ledger promotes warranty to realized, count it in gross too.
  if (warranty.amount !== null && warranty.lifecycle === "realized") {
    warranty.includedInGross = true;
  }

  const callback = ledgerLine("callback_labor", "callback_labor", {
    category: "callback_labor",
    label: "Callback Labor",
    amount: null,
    lifecycle: "realized",
    source: "expense_ledger",
    reliability: "missing",
    visibility: FINANCE_OPS,
    includedInGross: true,
    includedInNet: true,
    notes: "Callback events tracked; dollars not yet captured",
  });

  const direct: JobCostLine[] = [
    offerCompensation,
    materials,
    marketing,
    permits,
    financing,
    warranty,
    callback,
  ];

  // ── Phase C: auto-emit realized direct lines for ledger categories
  // that have no static slot above (dumpster, fuel, field-add, commission,
  // misc operational). Each is realized when present, missing otherwise. ──
  const LEDGER_AUX: Array<{
    ledger: ExpenseLedgerCategory;
    category: CostCategory;
    label: string;
    visibility: CostVisibility[];
  }> = [
    { ledger: "dumpster_disposal", category: "dumpster_disposal", label: "Dumpster / Disposal", visibility: FINANCE_OPS },
    { ledger: "fuel_logistics", category: "fuel_logistics", label: "Fuel / Logistics", visibility: FINANCE_OPS },
    { ledger: "field_add", category: "field_add", label: "Field Adds", visibility: FINANCE_OPS },
    { ledger: "sales_commission", category: "sales_commission", label: "Sales Commission", visibility: FINANCE_ONLY },
  ];

  for (const aux of LEDGER_AUX) {
    const bucket = ledger.byCategory[aux.ledger];
    if (!bucket || bucket.amount <= 0) continue;
    direct.push(
      buildLine({
        category: aux.category,
        label: aux.label,
        amount: bucket.amount,
        lifecycle: "realized",
        source: "expense_ledger",
        reliability: "actual",
        visibility: aux.visibility,
        includedInGross: true,
        includedInNet: true,
        notes:
          `${bucket.entryCount} entr${bucket.entryCount === 1 ? "y" : "ies"} · ` +
          `source: ${bucket.source}`,
      }),
    );
  }

  // ── Operating allocations — all untracked, NEVER subtracted silently ──
  const allocations: JobCostLine[] = [
    "admin_allocation",
    "warehouse_allocation",
    "vehicle_allocation",
    "software_allocation",
    "insurance_allocation",
    "office_allocation",
    "misc_allocation",
  ].map((cat) =>
    buildLine({
      category: cat as CostCategory,
      label: allocationLabel(cat as CostCategory),
      amount: null,
      lifecycle: "allocated",
      source: "allocator",
      reliability: "missing",
      visibility: FINANCE_ONLY,
      includedInGross: false,
      includedInNet: true,
      notes: "Allocator engine not built (Phase E)",
    }),
  );

  // Reserves are surfaced through `warranty` line above; separate bucket reserved
  // for future Phase D output.
  const reserves: JobCostLine[] = [];

  const lines = [...direct, ...allocations, ...reserves];

  // ── Totals ────────────────────────────────────────────────────────
  const sumKnown = (ls: JobCostLine[], pred: (l: JobCostLine) => boolean) =>
    ls.filter((l) => pred(l) && l.amount !== null).reduce((s, l) => s + (l.amount as number), 0);

  const directKnownTotal = sumKnown(direct, (l) => l.includedInGross && l.amount !== null);
  const allocationsKnownTotal = sumKnown(allocations, (l) => l.amount !== null);

  const projectedGrossContribution = hasContractValue
    ? contractValue - directKnownTotal
    : 0;
  const projectedGrossMargin = hasContractValue
    ? projectedGrossContribution / contractValue
    : 0;

  // Realized gross only if EVERY gross-eligible direct line is realized.
  const grossEligible = direct.filter((l) => l.includedInGross);
  const allGrossRealized =
    grossEligible.length > 0 &&
    grossEligible.every((l) => l.amount !== null && l.lifecycle === "realized");
  const realizedGrossContribution = allGrossRealized
    ? contractValue - directKnownTotal
    : null;

  const projectedNetContribution = projectedGrossContribution - allocationsKnownTotal;
  const projectedNetMargin = hasContractValue
    ? projectedNetContribution / contractValue
    : 0;

  const missingCostCount = lines.filter((l) => l.reliability === "missing").length;
  const projectedCostCount = lines.filter(
    (l) => l.amount !== null && (l.lifecycle === "projected" || l.lifecycle === "committed"),
  ).length;
  const realizedCostCount = lines.filter(
    (l) => l.amount !== null && l.lifecycle === "realized",
  ).length;

  const trackedCategories = lines
    .filter((l) => l.amount !== null)
    .map((l) => l.category);
  const untrackedCategories = lines
    .filter((l) => l.amount === null)
    .map((l) => l.category);

  return {
    jobId: job.id,
    generatedAt: new Date().toISOString(),
    contractValue,
    hasContractValue,
    lines,
    direct,
    allocations,
    reserves,
    totals: {
      contractValue,
      directKnownTotal,
      allocationsKnownTotal,
      projectedGrossContribution,
      projectedGrossMargin,
      realizedGrossContribution,
      projectedNetContribution,
      projectedNetMargin,
      missingCostCount,
      projectedCostCount,
      realizedCostCount,
    },
    coverage: { trackedCategories, untrackedCategories },
    disclaimer: DISCLAIMER,
  };
}

function allocationLabel(cat: CostCategory): string {
  switch (cat) {
    case "admin_allocation": return "Admin Allocation";
    case "warehouse_allocation": return "Warehouse Allocation";
    case "vehicle_allocation": return "Vehicle / Fuel Allocation";
    case "software_allocation": return "Software / Systems Allocation";
    case "insurance_allocation": return "Insurance Allocation";
    case "office_allocation": return "Office Allocation";
    case "misc_allocation": return "Misc Operational Allocation";
    default: return cat;
  }
}
